import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Card, Stack, Group, Text, Button, Textarea, Badge,
  ThemeIcon, Progress, Divider, Alert, Collapse, Table
} from '@mantine/core';
import {
  IconCheck, IconPencil, IconRotateClockwise,
  IconSparkles, IconBrain, IconChevronDown, IconChevronUp,
  IconAlertTriangle, IconCompass, IconArrowRight, IconInfoCircle
} from '@tabler/icons-react';
import AIGeneratingIcon from '../../components/AIGeneratingIcon';
import { selectInheritedBuilderRisks } from '../../redux/slices/risksSlice';
import { authFetch } from '../../utils/authFetch';

// Priority color mapping (consistent with app standard)
const PRIORITY_COLORS = {
  Critical: { bg: '#1A1A1A', text: '#fff' },
  High: { bg: '#993333', text: '#fff' },
  Significant: { bg: '#B94D4D', text: '#fff' },
  Moderate: { bg: '#D4AA7D', text: '#333' },
  Low: { bg: '#B8C2B7', text: '#333' },
  Negligible: { bg: '#D8D8D8', text: '#333' }
};

/**
 * NavigatorNarrativeCard - Narrative card for Navigator (use_case) assessments
 * Shows a read-only summary of use case context from step 1, then lets the user
 * explicitly trigger narrative generation. No redundant context form.
 */
export default function NavigatorNarrativeCard({
  modelId,
  assessmentUuid,
  parentAssessmentUuid,
  bucket,
  path,
  businessContext,
  useCase,
  onApproved
}) {
  // Step: summary | generating | ready | approved | failed
  const [status, setStatus] = useState('summary');
  const [narrative, setNarrative] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [inheritedRisksSummary, setInheritedRisksSummary] = useState('');
  const [showInheritedRisks, setShowInheritedRisks] = useState(true);
  const [showAllRisks, setShowAllRisks] = useState(false);
  const [focusAreas, setFocusAreas] = useState([]);

  // Get inherited model risks from Redux
  const reduxInheritedRisks = useSelector(state =>
    parentAssessmentUuid ? selectInheritedBuilderRisks(state, parentAssessmentUuid) : []
  );

  // Get inherited security scenarios from Redux
  const inheritedSecurityScenarios = useSelector(state => {
    if (!parentAssessmentUuid) return [];
    const scenarios = state.security?.byAssessment?.[parentAssessmentUuid]?.scenarios || {};
    return Object.values(scenarios).map(scenario => {
      const toString = (val, fallback = '') => {
        if (val === null || val === undefined) return fallback;
        if (typeof val === 'string') return val;
        if (typeof val === 'object') {
          return Object.keys(val).join(', ') || fallback;
        }
        return String(val);
      };

      return {
        riskId: scenario.scenarioId || scenario.id || `scenario_${Math.random().toString(36).slice(2)}`,
        riskName: toString(scenario.name, toString(scenario.scenarioName, 'Unnamed Scenario')),
        priority: toString(scenario.impact, toString(scenario.priority, 'Moderate')),
        area: 'Security Assessment',
        category: toString(scenario.attackSurface, 'Security'),
        type: 'SecurityScenario'
      };
    });
  });

  // Combine model risks + security scenarios
  const inheritedRisks = useMemo(() => {
    const modelRisks = reduxInheritedRisks.map(risk => ({
      ...risk,
      riskName: risk.riskName || risk.scenarioText || 'Unnamed Risk',
      area: risk.sectionName || risk.section || 'Unknown',
      priority: risk.priority || null
    }));
    return [...modelRisks, ...inheritedSecurityScenarios];
  }, [reduxInheritedRisks, inheritedSecurityScenarios]);

  const INITIAL_DISPLAY_COUNT = 10;
  const displayedRisks = showAllRisks ? inheritedRisks : inheritedRisks.slice(0, INITIAL_DISPLAY_COUNT);

  // Parse business context for summary display
  const contextSummary = useMemo(() => {
    if (!businessContext) return null;
    try {
      const parsed = JSON.parse(businessContext);
      if (typeof parsed === 'object' && parsed !== null) {
        // Template-based structured data — show as key-value pairs
        return { type: 'structured', data: parsed };
      }
    } catch {
      // Plain text description
    }
    return { type: 'text', data: businessContext };
  }, [businessContext]);

  // Generate Navigator narrative using props directly (no local form state)
  const generateNarrative = useCallback(async () => {
    try {
      setStatus('generating');
      setKeywords([]);
      setNarrative('');

      console.log('[NavigatorNarrativeCard] Generating Navigator narrative with payload:', {
        modelId,
        assessmentUuid,
        parentAssessmentUuid,
        bucket,
        path,
        hasBusinessContext: !!businessContext,
        hasUseCase: !!useCase
      });

      const res = await authFetch('/.netlify/functions/navigatorGenerateNarrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          parentAssessmentUuid,
          bucket,
          path,
          businessContext,
          useCase
        })
      });

      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Generation failed');
        throw new Error(errorText || 'Request failed');
      }

      const data = await res.json();
      if (data.success && data.narrative) {
        setNarrative(data.narrative);
        setInheritedRisksSummary(data.inheritedRisksSummary || '');
        setFocusAreas(data.focusAreas || []);

        const displayKeywords = (data.focusAreas || []).slice(0, 12)
          .map(a => a.replace(/([A-Z])/g, ' $1').trim());
        setKeywords(displayKeywords);
        setStatus('ready');
      } else {
        throw new Error(data.error || 'Narrative generation failed');
      }
    } catch (error) {
      console.error('[NavigatorNarrativeCard] Generation error:', error);
      setStatus('failed');
      setNarrative('');
    }
  }, [modelId, assessmentUuid, parentAssessmentUuid, bucket, path, businessContext, useCase]);

  const handleGenerateClick = useCallback(() => {
    if (!modelId || !assessmentUuid) {
      console.error('[NavigatorNarrativeCard] Missing modelId or assessmentUuid');
      return;
    }
    generateNarrative();
  }, [modelId, assessmentUuid, generateNarrative]);

  const handleApprove = () => {
    setStatus('approved');
    onApproved?.(narrative, focusAreas);
  };

  const handleRegenerate = () => {
    generateNarrative();
  };

  const statusBadge = (() => {
    switch (status) {
      case 'summary':
        return <Badge variant="light" color="orange">Review Context</Badge>;
      case 'generating':
        return (
          <Badge variant="light" color="orange">
            <Group gap="xs">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" />
              <Text size="sm">Generating...</Text>
            </Group>
          </Badge>
        );
      case 'ready':
        return <Badge variant="light" color="orange">Ready for Review</Badge>;
      case 'approved':
        return (
          <Badge variant="filled" color="orange">
            <Group gap="xs">
              <IconCheck size={14} />
              <Text size="sm">Approved</Text>
            </Group>
          </Badge>
        );
      case 'failed':
        return <Badge variant="light" color="red">Generation Failed</Badge>;
      default:
        return <Badge variant="outline" color="gray">Loading...</Badge>;
    }
  })();

  const borderColor = ({
    summary: '#fd7e14',
    generating: '#fd7e14',
    ready: '#fd7e14',
    approved: '#e8590c',
    failed: '#dc2626',
    loading: '#6b7280',
  })[status] || '#6b7280';

  const canEdit = status === 'ready' || status === 'approved';
  const canApprove = status === 'ready' || status === 'approved';
  const progress = status === 'approved' ? 100 : (status === 'ready' ? 100 : (status === 'generating' ? 50 : (status === 'summary' ? 10 : 25)));

  return (
    <Stack gap="lg">
      {/* Header with Progress */}
      <Card withBorder p="lg" style={{ backgroundColor: 'rgba(253, 126, 20, 0.05)' }}>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="md" align="center">
                <ThemeIcon size="xl" variant="gradient" gradient={{ from: 'orange', to: 'yellow' }}>
                  <IconCompass size={24} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={600} size="lg">Navigator Risk Analysis</Text>
                  <Text size="sm" c="dimmed">
                    Assessing use-case deployment risks
                  </Text>
                </Stack>
              </Group>
            </div>
            {statusBadge}
          </Group>
          <Progress value={progress} size="sm" color="orange" />
        </Stack>
      </Card>

      {/* Use Case Context Summary (read-only) */}
      {status === 'summary' && (
        <Card withBorder p="lg" style={{ borderColor: '#fd7e14' }}>
          <Stack gap="md">
            <Group gap="md">
              <ThemeIcon size="lg" variant="light" color="orange">
                <IconInfoCircle size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600}>Use Case Context Summary</Text>
                <Text size="sm" c="dimmed">
                  Review the context from step 1 before generating the risk narrative
                </Text>
              </Stack>
            </Group>

            <Divider />

            {contextSummary?.type === 'structured' ? (
              <Stack gap="xs">
                {Object.entries(contextSummary.data)
                  .filter(([, value]) => value && String(value).trim())
                  .map(([key, value]) => (
                    <Group key={key} gap="sm" align="flex-start" wrap="nowrap">
                      <Text size="sm" fw={500} c="dimmed" style={{ minWidth: 140 }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}:
                      </Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Text>
                    </Group>
                  ))}
              </Stack>
            ) : contextSummary?.type === 'text' ? (
              <Card withBorder p="md" bg="gray.0">
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {contextSummary.data}
                </Text>
              </Card>
            ) : (
              <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">No use case context was provided in step 1.</Text>
              </Alert>
            )}

            <Group justify="flex-end" mt="md">
              <Button
                color="orange"
                rightSection={<IconArrowRight size={16} />}
                onClick={handleGenerateClick}
              >
                Generate Narrative
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* Inherited Risks Panel */}
      <Card withBorder p="md" style={{ borderColor: parentAssessmentUuid ? '#fd7e14' : '#868e96' }}>
        <Stack gap="sm">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color={parentAssessmentUuid ? 'orange' : 'gray'}>
                <IconBrain size={14} />
              </ThemeIcon>
              <Text fw={500} size="sm">Technical Risks</Text>
              <Badge size="sm" variant="light" color={parentAssessmentUuid ? 'orange' : 'gray'}>
                {inheritedRisks.length} risks
              </Badge>
            </Group>
            {parentAssessmentUuid && inheritedRisks.length > 0 && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setShowInheritedRisks(!showInheritedRisks)}
                rightSection={showInheritedRisks ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              >
                {showInheritedRisks ? 'Hide' : 'Show'}
              </Button>
            )}
          </Group>

          {!parentAssessmentUuid ? (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">
                This is a standalone Navigator assessment. No Builder assessment was linked during creation.
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                To inherit technical risks, create a new Navigator assessment and select a completed Builder assessment.
              </Text>
            </Alert>
          ) : inheritedRisks.length === 0 ? (
            <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">
                No risks found in the linked Builder assessment.
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                The Builder assessment may not have saved risks yet, or the risks may still be loading.
              </Text>
            </Alert>
          ) : (
            <Collapse in={showInheritedRisks}>
              {inheritedRisksSummary && (
                <Text size="sm" c="dimmed" mb="sm">{inheritedRisksSummary}</Text>
              )}
              <Table striped highlightOnHover withTableBorder fontSize="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Risk</Table.Th>
                    <Table.Th>Area</Table.Th>
                    <Table.Th>Priority</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayedRisks.map((risk, idx) => {
                    const priorityConfig = PRIORITY_COLORS[risk.priority] || { bg: '#e9ecef', text: '#495057' };
                    return (
                      <Table.Tr key={idx}>
                        <Table.Td>{risk.riskName}</Table.Td>
                        <Table.Td>
                          <Badge
                            size="xs"
                            variant="light"
                            color={risk.area === 'Security Assessment' ? 'red' : 'gray'}
                          >
                            {risk.area}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="xs"
                            style={{
                              backgroundColor: priorityConfig.bg,
                              color: priorityConfig.text
                            }}
                          >
                            {risk.priority || 'Not Evaluated'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
              {inheritedRisks.length > INITIAL_DISPLAY_COUNT && (
                <Group justify="space-between" mt="xs">
                  <Text size="xs" c="dimmed">
                    Showing {displayedRisks.length} of {inheritedRisks.length} risks
                  </Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    onClick={() => setShowAllRisks(!showAllRisks)}
                  >
                    {showAllRisks ? 'Show Less' : `Show All ${inheritedRisks.length} Risks`}
                  </Button>
                </Group>
              )}
            </Collapse>
          )}
        </Stack>
      </Card>

      {/* Focus Areas */}
      {status !== 'summary' && focusAreas.length > 0 && (
        <Card withBorder p="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>Suggested Focus Areas</Text>
            <Group gap="xs">
              {focusAreas.map((area, idx) => (
                <Badge key={idx} variant="light" color="orange" size="sm">
                  {area.replace(/([A-Z])/g, ' $1').replace('Risk', '').trim()}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Card>
      )}

      {/* Narrative Content */}
      {status !== 'summary' && (
      <Card withBorder p="lg" style={{ borderColor }}>
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="orange">
                <IconSparkles size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Risk Narrative</Text>
            </Group>
            {canEdit && (
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPencil size={14} />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel Edit' : 'Edit'}
              </Button>
            )}
          </Group>

          {status === 'generating' && (
            <Stack align="center" py="xl">
              <AIGeneratingIcon isGenerating size={48} />
              <Text size="sm" c="dimmed">Analyzing use case context...</Text>
            </Stack>
          )}

          {status === 'failed' && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Stack gap="xs">
                <Text size="sm">Failed to generate narrative. Please try again.</Text>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  onClick={handleRegenerate}
                >
                  Retry
                </Button>
              </Stack>
            </Alert>
          )}

          {(status === 'ready' || status === 'approved') && (
            <>
              {isEditing ? (
                <Textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  minRows={10}
                  autosize
                />
              ) : (
                <Text
                  size="sm"
                  style={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}
                >
                  {narrative}
                </Text>
              )}

              {/* Keywords */}
              {keywords.length > 0 && (
                <>
                  <Divider />
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">Keywords:</Text>
                    {keywords.map((kw, idx) => (
                      <Badge key={idx} size="xs" variant="outline" color="orange">
                        {kw}
                      </Badge>
                    ))}
                  </Group>
                </>
              )}

              {/* Actions */}
              <Group justify="space-between" mt="md">
                <Button
                  variant="light"
                  size="sm"
                  leftSection={<IconRotateClockwise size={16} />}
                  onClick={handleRegenerate}
                  disabled={status === 'approved'}
                >
                  Regenerate
                </Button>
                {canApprove && status !== 'approved' && (
                  <Button
                    color="orange"
                    size="sm"
                    leftSection={<IconCheck size={16} />}
                    onClick={handleApprove}
                  >
                    Approve & Continue
                  </Button>
                )}
              </Group>
            </>
          )}
        </Stack>
      </Card>
      )}
    </Stack>
  );
}
